import { useEffect, useRef } from 'react';

// --- Extracted drawing helpers ---
function drawFrame(ctx, frameX, frameY, frameW, frameH) {
  const grad = ctx.createLinearGradient(frameX, frameY, frameX + frameW, frameY);
  grad.addColorStop(0, '#2A3A40');
  grad.addColorStop(0.5, '#4A5A60');
  grad.addColorStop(1, '#2A3A40');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.roundRect(frameX, frameY, frameW, frameH, 8);
  ctx.fill();
}

function drawGlassZones(ctx, glassX, filterX, glassW, frameY, frameH) {
  ctx.fillStyle = 'rgba(248,113,113,0.03)';
  ctx.fillRect(glassX, frameY + 8, filterX - glassX, frameH - 16);
  ctx.fillStyle = 'rgba(74,222,128,0.02)';
  ctx.fillRect(filterX, frameY + 8, glassX + glassW - filterX, frameH - 16);
  const haze = ctx.createLinearGradient(glassX, frameY, filterX, frameY);
  haze.addColorStop(0, 'rgba(180,160,140,0.15)');
  haze.addColorStop(1, 'rgba(180,160,140,0.03)');
  ctx.fillStyle = haze;
  ctx.fillRect(glassX, frameY + 8, filterX - glassX, frameH - 16);
}

function drawFilterStrip(ctx, filterX, frameY, frameH) {
  ctx.fillStyle = 'rgba(63,167,214,0.2)';
  ctx.fillRect(filterX - 2, frameY + 8, 4, frameH - 16);
  ctx.shadowColor = 'rgba(63,167,214,0.5)';
  ctx.shadowBlur = 15;
  ctx.fillRect(filterX - 1, frameY + 8, 2, frameH - 16);
  ctx.shadowBlur = 0;
}

function drawLeds(ctx, frameX, frameY, frameH, t) {
  for (let i = 0; i < 3; i++) {
    const dotY = frameY + frameH * 0.3 + i * 20;
    ctx.fillStyle = `rgba(63,167,214,${0.3 + Math.sin(t + i * 0.5) * 0.3})`;
    ctx.beginPath();
    ctx.arc(frameX + 5, dotY, 2, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawLabels(ctx, glassX, filterX, glassW, frameY, frameH) {
  ctx.font = '600 9px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(248,113,113,0.6)';
  ctx.fillText('OUTDOOR', glassX + (filterX - glassX) / 2, frameY + frameH - 15);
  ctx.fillStyle = 'rgba(74,222,128,0.6)';
  ctx.fillText('WITH VAYU', filterX + (glassX + glassW - filterX) / 2, frameY + frameH - 15);
  ctx.save();
  ctx.translate(filterX, frameY + frameH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillStyle = 'rgba(63,167,214,0.5)';
  ctx.font = '700 7px Inter, sans-serif';
  ctx.fillText('VAYU PANE', 0, -8);
  ctx.restore();
}

function updateParticle(p, filterX, t) {
  p.x += p.vx;
  p.y += p.vy + Math.sin(p.x * 0.03 + t) * 0.2;
  if (p.side === 'outside' && p.x >= filterX - 2 && !p.absorbed) {
    if (Math.random() < 0.85) { p.absorbed = true; p.vx = 0; }
    else { p.side = 'inside'; p.vx *= 0.3; p.size *= 0.3; p.opacity *= 0.2; }
  }
  if (p.absorbed) { p.opacity -= 0.03; p.size *= 0.98; }
}

function drawParticle(ctx, p) {
  if (p.absorbed) {
    ctx.fillStyle = `rgba(63,167,214,${p.opacity * 0.8})`;
    ctx.shadowColor = 'rgba(63,167,214,0.4)';
    ctx.shadowBlur = 4;
  } else if (p.side === 'outside') {
    ctx.fillStyle = `rgba(248,113,113,${p.opacity * 0.6})`;
    ctx.shadowBlur = 0;
  } else {
    ctx.fillStyle = `rgba(74,222,128,${p.opacity * 0.4})`;
    ctx.shadowBlur = 0;
  }
  ctx.beginPath();
  ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
}

// --- Main component ---
export default function VayuPaneViewer() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    let animId;
    const particles = [];

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const animate = () => {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width, h = rect.height;
      const frameX = w * 0.15, frameW = w * 0.7, frameY = h * 0.08, frameH = h * 0.84;
      const glassX = frameX + 8, glassW = frameW - 16;
      const filterX = glassX + glassW * 0.48;
      const t = Date.now() * 0.001;

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = '#061A1F';
      ctx.fillRect(0, 0, w, h);

      drawFrame(ctx, frameX, frameY, frameW, frameH);
      drawGlassZones(ctx, glassX, filterX, glassW, frameY, frameH);
      drawFilterStrip(ctx, filterX, frameY, frameH);
      drawLeds(ctx, frameX, frameY, frameH, t);

      if (Math.random() < 0.15) {
        particles.push({
          x: glassX + Math.random() * 20, y: frameY + 20 + Math.random() * (frameH - 40),
          vx: 0.4 + Math.random() * 0.8, vy: (Math.random() - 0.5) * 0.3,
          size: 1.5 + Math.random() * 3, opacity: 0.4 + Math.random() * 0.5,
          side: 'outside', absorbed: false,
        });
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        updateParticle(particles[i], filterX, t);
        if (particles[i].opacity <= 0 || particles[i].x > glassX + glassW + 10) { particles.splice(i, 1); continue; }
        drawParticle(ctx, particles[i]);
      }
      if (particles.length > 150) particles.splice(0, particles.length - 150);

      drawLabels(ctx, glassX, filterX, glassW, frameY, frameH);
      animId = requestAnimationFrame(animate);
    };

    animate();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(animId); };
  }, []); // Animation loop: runs once, uses only local variables

  return (
    <div className="relative rounded-3xl overflow-hidden" style={{ boxShadow: '0 30px 80px rgba(63,167,214,0.08)', border: '1px solid rgba(63,167,214,0.1)' }} data-testid="vayu-pane-viewer">
      <canvas ref={canvasRef} className="w-full" style={{ height: 380, background: '#061A1F' }} />
      <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full" style={{ background: 'rgba(6,26,31,0.8)', border: '1px solid rgba(63,167,214,0.15)' }}>
        <span className="text-[10px] font-bold" style={{ color: 'var(--aqua)' }}>Interactive 3D View</span>
      </div>
    </div>
  );
}
